import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const API_BASE_URL = 'https://spaceportfolio-api.rajansudhan0.workers.dev';

const TestimonialManager = ({ adminKey }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/testimonials`, { 
      cache: 'no-store',
      headers: { 'X-Admin-Key': adminKey }
    })
      .then(res => res.json())
      .then(data => {
        setTestimonials(data);
        setLoading(false);
      });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    if (data.id) data.id = parseInt(data.id);
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/testimonials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'Saved successfully!' });
        setTimeout(() => setEditing(null), 1000);
        fetchTestimonials();
      } else {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Save failed');
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/testimonials/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });
      if (res.ok) fetchTestimonials();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <div className="h-64 bg-white/5 rounded-2xl animate-pulse"></div>;

  return (
    <div className="space-y-6 pb-12">
      <div className="flex justify-end">
        <button 
          onClick={() => {
            setEditing({});
            setMessage(null);
          }}
          className="bg-accent/10 hover:bg-accent/20 text-accent px-4 py-2 rounded-xl border border-accent/20 transition-smooth flex items-center gap-2"
        >
          <Icon name="Plus" size={20} />
          Add New Testimonial
        </button>
      </div>

      <div className="grid gap-6">
        {testimonials.map((item) => (
          <div key={item.id} className="bg-surface/50 border border-white/10 p-6 rounded-2xl group flex flex-col gap-4">
            <div className="flex justify-between items-start">
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden">
                  {item.image || item.avatar ? <img src={item.image || item.avatar} className="w-full h-full object-cover" /> : <Icon name="User" size={24} />}
                </div>
                <div>
                  <h4 className="font-bold text-text-primary">{item.name}</h4>
                  <p className="text-sm text-text-secondary">{item.role || item.position} at {item.company}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => {
                    setEditing(item);
                    setMessage(null);
                  }} 
                  className="p-2 hover:bg-white/5 rounded-lg text-text-secondary hover:text-accent transition-smooth"
                >
                  <Icon name="Edit" size={18} />
                </button>
                <button onClick={() => handleDelete(item.id)} className="p-2 hover:bg-white/5 rounded-lg text-text-secondary hover:text-red-500 transition-smooth"><Icon name="Trash2" size={18} /></button>
              </div>
            </div>
            <p className="text-text-secondary italic text-sm line-clamp-3">"{item.quote || item.content}"</p>
            {item.result_metric && <div className="text-xs font-semibold text-accent uppercase tracking-wider">{item.result_metric}</div>}
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-white/10 rounded-3xl p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold mb-6">{editing.id ? 'Edit' : 'Add'} Testimonial</h3>
            <form onSubmit={handleSave} className="space-y-4">
              {editing.id && <input type="hidden" name="id" value={editing.id} />}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1 text-text-secondary">Name</label>
                  <input name="name" defaultValue={editing.name} required className="w-full bg-background border border-white/10 rounded-lg px-4 py-2 outline-none focus:border-accent" />
                </div>
                <div>
                  <label className="block text-sm mb-1 text-text-secondary">Role / Position</label>
                  <input name="role" defaultValue={editing.role || editing.position} className="w-full bg-background border border-white/10 rounded-lg px-4 py-2 outline-none focus:border-accent" />
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1 text-text-secondary">Company / Organization</label>
                <input name="company" defaultValue={editing.company} className="w-full bg-background border border-white/10 rounded-lg px-4 py-2 outline-none focus:border-accent" />
              </div>

              <div>
                <label className="block text-sm mb-1 text-text-secondary">Testimonial Quote</label>
                <textarea name="quote" defaultValue={editing.quote || editing.content} required className="w-full bg-background border border-white/10 rounded-lg px-4 py-2 outline-none focus:border-accent h-32" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1 text-text-secondary">Avatar URL</label>
                  <input name="image" defaultValue={editing.image || editing.avatar} className="w-full bg-background border border-white/10 rounded-lg px-4 py-2 outline-none focus:border-accent" />
                </div>
                <div>
                  <label className="block text-sm mb-1 text-text-secondary">Result Metric (Badge)</label>
                  <input name="result_metric" defaultValue={editing.result_metric} placeholder="e.g. 500+ Students" className="w-full bg-background border border-white/10 rounded-lg px-4 py-2 outline-none focus:border-accent" />
                </div>
              </div>

              {message && (
                <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
                  message.type === 'success' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'
                }`}>
                  <Icon name={message.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
                  <span>{message.text}</span>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-6">
                <button type="button" onClick={() => setEditing(null)} className="px-6 py-2 rounded-lg text-text-secondary hover:bg-white/5 transition-smooth">Cancel</button>
                <button type="submit" disabled={saving} className="bg-accent text-white px-8 py-2 rounded-lg font-semibold hover:bg-accent/90 transition-smooth disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialManager;
